<template>
  <v-dialog :value="value" persistent max-width="750px">
    <v-card width="1000">
      <v-card-title>
        <span>Confirm Linked Group Update</span>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :height="500"
        :loading="loading"
        :items-per-page="-1"
        v-model="selectedItems"
        show-select
        no-data-text="No items found for this linked group at this store."
        hide-default-footer>
        <template #item.new_multiplier>
          <td class="noWrap">{{ newMultiplier }}</td>
        </template>
        <template #item.new_srp>
          <td class="noWrap">${{ newSrp | format }}</td>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <v-card-actions flat>
        <v-spacer />
        <v-btn
          text
          @click="closeModal">
          Cancel
        </v-btn>
        <v-btn
          class="blue white--text mr-2"
          @click="onApply" :disabled="loading">
          Apply changes and Save selected Items
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Required retail confirmation -->
    <v-dialog :value="showComplianceConfirm" persistent max-width="500px">
      <v-card>
        <v-card-title>Above the Required Retail</v-card-title>
        <v-card-text>
          <p>{{ complianceWarningText }}</p>
          <v-checkbox
            v-model="complianceConfirmed"
            label="I understand that those stores will give up the ePay allowance on these items."
            hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showComplianceConfirm = false">Cancel</v-btn>
          <v-btn class="blue white--text mr-2" :disabled="!complianceConfirmed" @click="confirmCompliance">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Excluded items prompt -->
    <v-dialog :value="showExcludedPrompt" persistent max-width="650px">
      <v-card>
        <v-card-title>Include Excluded Items?</v-card-title>
        <v-card-text>
          The following items in this linked group are currently excluded. Select any you would like to include back in the batch.
        </v-card-text>
        <v-data-table
          :headers="excludedHeaders"
          :items="excludedItems"
          :items-per-page="-1"
          v-model="selectedExcludedItems"
          show-select
          hide-default-footer
          dense
        />
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="skipExcluded">Skip</v-btn>
          <v-btn class="blue white--text mr-2" @click="includeSelectedAndSave" :disabled="selectedExcludedItems.length === 0">
            Include selected items in batch
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
import Items from '@/axios/items';
import Rules from '@/axios/rules-management'
import { shared } from '@/mixins/shared'
import { utils } from '@/mixins/utils'
import { tprItemRow } from '@/mixins/tprItemRow'
export default {
  name: 'LinkedGroupModal',
  mixins: [shared, utils, tprItemRow],
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) },
    batch: { type: Object, default: () => ({}) },
    batchItems: { type: Array, default: () => ([]) },
    store: { type: Object, default: () => ({}) },
    isTprProRetailUser: { type: Boolean, default: false },
    storeGroupIsSelected: { type: Boolean, default: false },
    propagateChangesToChildRows: { type: Boolean, default: false }
  },
  data() {
    return {
      batchId: null,
      items: [],
      linkedGroup: null,
      loading: false,
      newMultiplier: null,
      newSrp: null,
      selectedItems: [],
      storeId: null,
      showExcludedPrompt: false,
      showComplianceConfirm: false,
      complianceConfirmed: false,
      complianceWarningText: '',
      excludedItems: [],
      selectedExcludedItems: [],
      excludedHeaders: [
        { value: 'data-table-select', class: 'accent white--text', align: "center" },
        { text: "Store", value: "store_name", class: "accent white--text", align: "start" },
        { text: "Item ID", value: "item_id", class: "accent white--text", align: "start" },
        { text: "UPC", value: "upc", class: "accent white--text", align: "start" },
        { text: "Description", value: "description", class: "accent white--text", align: "start" }
      ],
      headers: [
        { value: 'data-table-select', class: 'accent white--text', align: "center" },
        { text: "Item ID", value: "item_id", class: "accent white--text", align: "start" },
        { text: "UPC", value: "upc", class: "accent white--text", align: "start" },
        { text: "Description", value: "description", class: "accent white--text", align: "start" },
        { text: "Multiplier", value: "new_multiplier", class: "accent white--text", align: "start" },
        { text: "SRP", value: "new_srp", class: "accent white--text", align: "start" }
      ]
    }
  },
  async created() {
    this.batchId = this.batch.id
    this.linkedGroup = this.item.linked_group
    if (this.isTprProRetailUser) {
      this.storeId = this.store?.store_party_id || this.store?.party_id || this.item?.store_id || this.item?.store_party_id
      this.batchId = this.batch.tpr_batch_id
    }
    this.newMultiplier = this.item.multiplier
    this.newSrp = this.item.promotion_price
    this.getLinkedItems()
  },
  computed: {
    complianceBatch() {
      return this.batch
    }
  },
  methods: {
    async getLinkedItems() {
      const payload = {
        linked_group: [this.linkedGroup],
        tpr_date: this.batch.tpr_date
      }
      if (this.isTprProRetailUser) payload.store_ids = [this.storeId]
      try {
        this.loading = true
        const { data } = await Items.getItems(this.batchId, payload, 500, 0)
        this.items = data
        this.selectedItems = this.items
        await Promise.all(this.items.map(async (item) => {
          item.net_gross_profit = await this.calculateGrossProfit(item)
        }))
      } catch (err) {
        console.error("Error retrieving linked group items")
        const message = err?.response?.data?.message || err.message
        this.alert(`Error retrieving items: ${message}`)
      } finally {
        this.loading = false
      }
    },
    async calculateGrossProfit(item) {
      await this.ensureEnforcedPromos(item, this.batchId, item.store_id || this.storeId)
      const isPerishable = String(this.complianceBatch?.batch_type || '').toUpperCase() === 'PERISHABLE'
      return this.calculateNetGrossProfit(item, this.newSrp, this.newMultiplier, isPerishable)
    },
    // A retailer may apply an SRP above the required retail and give up the ePay allowance,
    // but confirms it first -- before the excluded-items prompt, which writes exclusions.
    onApply() {
      if (this.isTprProRetailUser) {
        const blocked = this.applyBlockedReason(this.selectedItems)
        if (blocked) {
          this.alert(blocked)
          return
        }
        const warned = this.complianceWarningRows()
        if (warned.length) {
          const names = [...new Set(warned.map(row => row.item_id))]
          this.complianceWarningText = `$${this.newSrp} is above the required retail for ${warned.length} item(s): ` +
            `${names.slice(0, 5).join(', ')}${names.length > 5 ? '...' : ''}. ` +
            'You can still apply it, but those stores will give up the ePay allowance on them.'
          this.complianceConfirmed = false
          this.showComplianceConfirm = true
          return
        }
      }
      this.checkExcludedItems()
    },
    confirmCompliance() {
      this.showComplianceConfirm = false
      this.checkExcludedItems()
    },
    // Rows this apply could price above their required retail: the selected items, the excluded
    // ones the next prompt can include, and, when propagating, every store row under them.
    complianceWarningRows() {
      const rows = [...this.selectedItems, ...this.items.filter(i => this.rowIsExcluded(i) && !this.selectedItems.includes(i))]
      if (this.propagateChangesToChildRows) {
        for (const item of [...rows]) rows.push(...(this.findBatchRow(item.id)?.alternate_store_items || []))
      }
      return rows.filter(row => !this.srpIsCompliant({
        ...row,
        promotion_price: this.newSrp,
        multiplier: this.newMultiplier
      }))
    },
    checkExcludedItems() {
      const excluded = this.items.filter(i => i.excluded && i.excluded !== '')
      if (this.propagateChangesToChildRows) {
        const seen = new Set(excluded.map(i => i.id))
        for (const item of this.items) {
          const parentItem = this.findBatchRow(item.id)
          if (parentItem?.alternate_store_items?.length) {
            for (const altItem of parentItem.alternate_store_items) {
              if (altItem.excluded && altItem.excluded !== '' && !seen.has(altItem.id)) {
                seen.add(altItem.id)
                excluded.push(altItem)
              }
            }
          }
        }
      }
      this.excludedItems = excluded
      if (this.excludedItems.length > 0) {
        this.selectedExcludedItems = []
        this.showExcludedPrompt = true
      } else {
        this.updateItems()
      }
    },
    async includeSelectedAndSave() {
      this.showExcludedPrompt = false
      // Judge the apply against the selection this is about to create, before writing the
      // exclusions. Re-included rows are typically the $0 or above-retail ones, so they are
      // the likeliest to breach the ceiling -- refusing downstream of the write would
      // routinely leave them included at the old price.
      const prospective = [...this.selectedItems]
      for (const item of this.selectedExcludedItems) {
        if (!prospective.includes(item)) prospective.push(item)
      }
      const blocked = this.applyBlockedReason(prospective)
      if (blocked) {
        this.alert(blocked)
        return
      }
      let focalExcluded, childExcluded
      if (this.propagateChangesToChildRows) {
        const focalItemIds = new Set(this.items.map(i => i.id))
        focalExcluded = this.selectedExcludedItems.filter(i => focalItemIds.has(i.id))
        childExcluded = this.selectedExcludedItems.filter(i => !focalItemIds.has(i.id))
      } else {
        focalExcluded = this.selectedExcludedItems
        childExcluded = []
      }
      const payload = this.selectedExcludedItems.map(item => ({ id: item.id, excluded: '' }))
      const childPayload = []
      if (this.propagateChangesToChildRows) {
        const allItemsToInclude = [...this.selectedExcludedItems, ...this.selectedItems]
        const seen = new Set(payload.map(p => p.id))
        for (const item of this.excludedItems) {
          if (!this.selectedExcludedItems.includes(item)) seen.add(item.id)
        }
        for (const item of allItemsToInclude) {
          const parentItem = this.findBatchRow(item.id)
          if (parentItem?.alternate_store_items?.length) {
            for (const altItem of parentItem.alternate_store_items) {
              if (altItem.excluded && altItem.excluded !== '' && !seen.has(altItem.id)) {
                seen.add(altItem.id)
                childPayload.push({ id: altItem.id, excluded: '' })
              }
            }
          }
        }
      }
      try {
        const fullPayload = [...payload, ...childPayload]
        if (this.isTprProRetailUser) {
          await Rules.saveBulkExclusions(fullPayload)
        } else {
          await Items.updateExcludeIncludeBulk(fullPayload)
        }
        for (const item of focalExcluded) {
          item.excluded = ''
          if (!this.selectedItems.includes(item)) {
            this.selectedItems.push(item)
          }
        }
        for (const item of childExcluded) {
          item.excluded = ''
        }
        if (this.propagateChangesToChildRows) {
          for (const entry of childPayload) {
            const altItem = this.batchItems.flatMap(bi => bi.alternate_store_items || []).find(a => a.id === entry.id)
            if (altItem) altItem.excluded = ''
          }
        }
        const allIncludedIds = [...focalExcluded.map(i => i.id), ...childExcluded.map(i => i.id)]
        this.$emit('onIncluded', allIncludedIds)
      } catch (err) {
        const message = err?.response?.data?.message || err.message
        this.alert(`Error including items: ${message}`)
      }
      this.updateItems()
    },
    skipExcluded() {
      this.showExcludedPrompt = false
      this.updateItems()
    },
    // A linked-group row sits either at the top level of batchItems or nested under a focal
    // row as one of its alternates. Opening this modal from a promoted store row scopes the
    // fetch to that store, and that store's rows are nested wherever the focal store also
    // has a row for the item -- so a top-level-only lookup called them unreachable and
    // refused the whole apply, blaming a search filter that was not there.
    findBatchRow(id) {
      const top = this.batchItems.find(bi => bi.id === id)
      if (top) return top
      for (const bi of this.batchItems) {
        const nested = bi.alternate_store_items?.find(asi => asi.id === id)
        if (nested) return nested
      }
      return null
    },
    // Both refusals below abort the whole apply, so they have to be answerable before
    // anything is written. includeSelectedAndSave persists exclusions before it gets here,
    // and a refusal after that write would leave items included in the batch at their old
    // price -- the half-applied state these guards exist to prevent. Returns the message to
    // show and abort on, or null to proceed. Must stay free of side effects.
    applyBlockedReason(sourceItems) {
      const altRows = []
      const storeRows = []
      const unreachable = []
      for (const item of sourceItems) {
        const parentItem = this.findBatchRow(item.id)
        if (this.propagateChangesToChildRows) {
          // A linked item we hold no row for cannot be cascaded. That used to pass silently.
          if (!parentItem) unreachable.push(item.item_id)
          else if (parentItem.alternate_store_items?.length > 0) {
            altRows.push(...parentItem.alternate_store_items.filter(asi => !this.rowIsExcluded(asi)))
          }
        }
        if (Number(item.promotion_price) != Number(this.newSrp) || Number(item.multiplier) !== Number(this.newMultiplier)) {
          storeRows.push(item)
        }
      }

      // Saying nothing here is how a half-applied linked group looked like a success:
      // the focal store took the new SRP and the other stores kept the old one.
      if (unreachable.length) {
        return `Cannot apply to the other stores in this group for ${unreachable.length} item(s): ${unreachable.slice(0, 5).join(', ')}${unreachable.length > 5 ? '...' : ''}. Clear any search filters and try again.`
      }

      // Same pass-through the Save button enforces on a focal row; applying an SRP across a
      // linked group must not be a way around it.
      const breaches = [...storeRows, ...altRows].filter(row => !this.passPercentIsCompliant({
        ...row,
        promotion_price: this.newSrp,
        multiplier: this.newMultiplier
      }))
      if (breaches.length) {
        const names = [...new Set(breaches.map(b => b.item_id))].slice(0, 5)
        const share = this.passThroughShareFor(breaches)
        return `$${this.newSrp} does not pass ${share} through to the consumer for ${breaches.length} item(s): ${names.join(', ')}${breaches.length > 5 ? '...' : ''}. No items were changed.`
      }
      return null
    },
    async updateItems() {
      const blocked = this.applyBlockedReason(this.selectedItems)
      if (blocked) {
        this.alert(blocked)
        return
      }
      this.loading = true
      const differingAltItems = []
      const differentStoreItems = []
      const altRecalcs = []
      for (const item of this.selectedItems) {
        const itemDiffers = Number(item.promotion_price) != Number(this.newSrp) || Number(item.multiplier) !== Number(this.newMultiplier)
        const itemAltItems = this.findBatchRow(item.id)
        if (this.propagateChangesToChildRows) {
          // Cascade mode: always persist children (the in-memory diff can't tell saved from propagated).
          // applyBlockedReason has already refused the apply if any row here is unreachable.
          if (itemAltItems?.alternate_store_items?.length > 0) {
            for (const altItem of itemAltItems.alternate_store_items) {
              altRecalcs.push(this.calculateGrossProfit(altItem).then(gp => { altItem.net_gross_profit = gp }))
              differingAltItems.push(altItem)
            }
          }
        }
        if (itemDiffers) differentStoreItems.push(item)
      }
      await Promise.all(altRecalcs)
      const differingItems = [...differentStoreItems, ...differingAltItems]

      if (differingItems.length <= 0) {
        this.notify("SRP and Multiplier is already set to given values for each item in Linked Group")
        this.closeModal()
        return
      }
      const payload = differingItems.map(item => {
        return {
          id: item.id,
          promotion_price: this.newSrp,
          multiplier: this.newMultiplier,
          note: item.note,
          net_gross_profit: item.net_gross_profit
        }
      })
      try {
        let saved
        if (this.isTprProRetailUser) {
          const { data } = await Rules.storeSrpMultiplier(payload)
          saved = data.message === 'TPR successfully updated.'
        } else {
          const { data } = await Items.tprUpdate(payload)
          saved = data.updated > 0
        }
        // Only report a save that happened. onSave clears the pending edits these rows
        // satisfied and reloads, so emitting it on a refusal the API reported without
        // throwing would discard unsaved work and show the user the old values back.
        if (!saved) {
          this.alert("Linked Group items were not updated.")
          return
        }
        this.notify("Linked Group items have been updated successfully")
        const updatedDifferingItems = differingItems.map(item => {
          return {
            ...item,
            promotion_price: this.newSrp,
            multiplier: this.newMultiplier
          }
        })
        this.$emit('onSave', updatedDifferingItems)
      } catch (err) {
        console.error("Error updating items:")
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating items: ${message}`)
      } finally {
        this.closeModal()
      }
    },
    closeModal() {
      this.newMultiplier = null
      this.newSrp = null
      this.items = []
      this.selectedItems = []
      this.$emit("update:value", false)
    },
  },
}
</script>